import { Trans, useLingui } from '@lingui/react/macro';
import { Plus, Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

/**
 * Landing page header — left title, centred search, right create button.
 *
 * Matches the pattern used in WFM settings pages (e.g. company documents)
 * with the addition of a centre-aligned search input, the same as the staff
 * list page. The search is debounced by its caller; this is a pure
 * presentation component.
 */
export function TemplatesHeader({ search, onSearchChange, onCreate, title }: TemplatesHeaderProps) {
  const { t } = useLingui();
  return (
    <header className="grid grid-cols-1 items-center gap-4 sm:grid-cols-[1fr_auto_1fr] rotaready:min-h-[48px] rotaready:border-b rotaready:border-b-[#bbc3ca] rotaready:px-5">
      <h1 className="min-w-0 text-[length:var(--heading-font-size)] leading-[var(--heading-line-height)] font-[var(--heading-font-weight)] tracking-tight text-[var(--heading-foreground)]">
        {title}
      </h1>
      <div className="relative w-full max-w-md sm:justify-self-center rotaready:max-w-[350px]">
        <Search className="text-muted-foreground absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2" />
        <Input
          aria-label={t`Search templates`}
          placeholder={t`Search templates`}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className={cn(
            'pl-8 pr-4',
            'rotaready:h-[30px] rotaready:py-[7px] rotaready:pl-7 rotaready:pr-7',
          )}
        />
        {search && (
          <Button
            variant="ghost"
            size="icon"
            aria-label={t`Clear search`}
            className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 rotaready:right-2 rotaready:h-5 rotaready:w-5"
            onClick={() => onSearchChange('')}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
      <div className="flex items-center justify-end gap-2">
        <Button onClick={onCreate} className="rotaready:h-[30px]">
          <Plus className="mr-2 h-4 w-4 rotaready:hidden" /> <Trans>Create template</Trans>
        </Button>
      </div>
    </header>
  );
}

interface TemplatesHeaderProps {
  /** Page title, already translated by the caller. */
  title: string;
  /** Current search string. */
  search: string;
  /** Called whenever the search input changes. */
  onSearchChange: (value: string) => void;
  /** Called when the user clicks the create button. */
  onCreate: () => void;
}
